import { useMemo } from 'react';
import { MBFormattedText } from '@shared/components/MBText';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { getAnchorName, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { useIsDesktop } from '@shared/util/hooks';
import { notNil } from '@shared/util/notNil';
import { Styles } from '../styles';
import { MessageHoverContent } from './MessageHoverContent';
import { MBBlinkingBackground } from './blinkingBackground';
import { MBLinkPreviewFromTokens } from './linkPreviewFromTokens';

interface MBMessageBubbleContentProps {
  message: API.ChatMessageFragment;
  theme: MBMessageBubbleTheme;
  layout: MBChatMessageLayoutOptions;
  channelSlug: string;
  hovering: boolean;
  isFirstMessageInGroup: boolean;
  // TODO(fant): remove when link preview meta is supplied by the backend
  isHidingLinkPreview?: boolean;
  anchorName: string;
}

export function MBMessageBubbleContent({
  message,
  theme,
  layout,
  channelSlug,
  hovering,
  isHidingLinkPreview,
  isFirstMessageInGroup,
  anchorName,
}: MBMessageBubbleContentProps) {
  const isDesktop = useIsDesktop();
  const showHoverContentWhenRight = hovering && isDesktop;
  const showHoverContentWhenLeft = hovering || !message.meta.reactions.length;
  const tokens = useMemo(() => message.meta.tokens.filter(notNil), [message.meta.tokens]);

  if (!message.content) {
    return null;
  }

  const reactionPickerAnchorName = getAnchorName('reaction-button-bubble', message.id);

  return (
    <MBView
      fullWidth
      centerHorizontally
      style={[
        theme.align === 'left' && Styles.rowAlignLeft,
        theme.align === 'right' && Styles.rowAlignRight,
        !isFirstMessageInGroup && { paddingTop: grid(0.5) },
      ]}
      testID="chat-message-bubble-content"
    >
      <MBBlinkingBackground
        from={theme.blinkBackgroundColor}
        to={theme.backgroundColor}
        style={[
          Styles.messageBubble,
          Styles.bubbleMaxWidth,
          { opacity: theme.opacity },
          layout.isGroupedWithAbove && theme.align === 'left' && Styles.smallTopLeftBorderRadius,
          layout.isGroupedWithBelow && theme.align === 'left' && Styles.smallBottomLeftBorderRadius,
          layout.isGroupedWithAbove && theme.align === 'right' && Styles.smallTopRightBorderRadius,
          layout.isGroupedWithBelow &&
            theme.align === 'right' &&
            Styles.smallBottomRightBorderRadius,
        ]}
      >
        <View style={{ anchorName }}>
          <MBView style={Styles.messageBubbleContent}>
            <MBFormattedText
              color={theme.textColor}
              highlightColor={theme.highlightColor}
              style={Styles.maxWidth}
              tokens={tokens}
            >
              {message.content}
            </MBFormattedText>
          </MBView>

          {!isHidingLinkPreview && (
            <MBLinkPreviewFromTokens
              message={message}
              align={theme.align}
              isGroupedWithBelow={layout.isGroupedWithBelow}
            />
          )}
        </View>
      </MBBlinkingBackground>
      <MessageHoverContent
        showContent={theme.align === 'right' ? showHoverContentWhenRight : showHoverContentWhenLeft}
        align={theme.align}
        message={message}
        channelSlug={channelSlug}
        reactionPickerAnchorName={reactionPickerAnchorName}
      />
    </MBView>
  );
}
